import React, { useState, useRef, useEffect, useMemo } from 'react'

function Remarks({
  remarksData,
  setRemarksData,
  blockItemData = [],
  filteredBlockItems = [],
  isLoadingBlockItems = false,
  debouncedBlockItemSearch,
  loadMoreBlockItemData,
  hasMoreBlockItems = false,
  isLoadingMoreBlockItems = false,
  selectedItemsFromTable = [],
  remarksList = [],
  availableColumns = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13] // Default columns for FixedAssets
}) {
  const [isItemsDropdownOpen, setIsItemsDropdownOpen] = useState(false);
  const [itemsSearchTerm, setItemsSearchTerm] = useState('');
  const [selectedItemIndex, setSelectedItemIndex] = useState(-1);
  const itemsDropdownRef = useRef(null);
  const itemsItemRefs = useRef({});
  const justOpenedItemsRef = useRef(false);
  const touchStartRef = useRef(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRemarksData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle Items dropdown search
  const handleItemsSearchChange = (value) => {
    setItemsSearchTerm(value);
    // Search only within selected items from table
    if (debouncedBlockItemSearch) {
      debouncedBlockItemSearch(value);
    }
  };

  // Handle Items dropdown item selection
  const handleItemsItemSelect = (selectedItem) => {
    if (selectedItem) {
      // Handle "ALL", "SELECT_REMARKS", and "SELECT_ITEM" options specially
      if (selectedItem === 'ALL' || selectedItem === 'SELECT_REMARKS' || selectedItem === 'SELECT_ITEM') {
        setRemarksData(prev => ({
          ...prev,
          remarksBlock: selectedItem
        }));
      } else {
        setRemarksData(prev => ({
          ...prev,
          remarksBlock: selectedItem.item_name || ''
        }));
      }
      // Clear search term when item is selected so placeholder logic works correctly
      setItemsSearchTerm('');
    }
    setIsItemsDropdownOpen(false);
    setSelectedItemIndex(-1);
  };

  // Open Items dropdown
  const openItemsDropdown = () => {
    setIsItemsDropdownOpen(true);
    justOpenedItemsRef.current = true;
    setTimeout(() => {
      justOpenedItemsRef.current = false;
    }, 300);
  };

  // Close Items dropdown
  const closeItemsDropdown = () => {
    setIsItemsDropdownOpen(false);
    setSelectedItemIndex(-1);
  };

  // Toggle Items dropdown
  const toggleItemsDropdown = () => {
    if (!justOpenedItemsRef.current) {
      setIsItemsDropdownOpen(prev => !prev);
    }
  };

  // Filter items to show only those selected in Types of Assets table and apply search filter
  const getAvailableItems = () => {
    let availableItems = [];
    
    if (filteredBlockItems && filteredBlockItems.length > 0) {
      availableItems = filteredBlockItems.filter(item =>
        selectedItemsFromTable.includes(item.item_name)
      );
    } else if (selectedItemsFromTable && selectedItemsFromTable.length > 0) {
      availableItems = selectedItemsFromTable.map(itemName => ({
        item_name: itemName
      }));
    }
    
    // Apply search filter if search term exists
    if (itemsSearchTerm && itemsSearchTerm.trim() !== '') {
      const searchLower = itemsSearchTerm.toLowerCase().trim();
      availableItems = availableItems.filter(item => {
        const itemName = item.item_name || '';
        const blockItem = item.block_item || '';
        return itemName.toLowerCase().includes(searchLower) || 
               blockItem.toLowerCase().includes(searchLower);
      });
    }
    
    return availableItems;
  };

  // Handle keyboard navigation for Items dropdown
  const handleItemsKeyDown = (e) => {
    const currentSelected = selectedItemIndex;
    const filteredList = getAvailableItems();
    // +1 for "Select Item", +1 for "ALL" (always shown)
    const totalItems = filteredList.length + 2;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isItemsDropdownOpen) {
          openItemsDropdown();
        }
        if (currentSelected < totalItems - 1) {
          setSelectedItemIndex(currentSelected + 1);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!isItemsDropdownOpen) {
          openItemsDropdown();
        }
        if (currentSelected > 0) {
          setSelectedItemIndex(currentSelected - 1);
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (isItemsDropdownOpen) {
          if (currentSelected === 0) {
            // "Select Item" option
            handleItemsItemSelect('SELECT_ITEM');
          } else if (currentSelected === 1) {
            // "ALL" option (always available)
            handleItemsItemSelect('ALL');
          } else {
            // Regular item selected (index - 2 because "Select Item" and "ALL" are at 0 and 1)
            const itemIndex = currentSelected - 2;
            if (itemIndex >= 0 && itemIndex < filteredList.length) {
              handleItemsItemSelect(filteredList[itemIndex]);
            }
          }
        } else if (!isItemsDropdownOpen) {
          openItemsDropdown();
        }
        break;
      case 'Escape':
        e.preventDefault();
        closeItemsDropdown();
        break;
      default:
        if (!isItemsDropdownOpen && e.key.length === 1) {
          openItemsDropdown();
        }
        break;
    }
  };

  // Get selected item name for display
  const getSelectedItemName = () => {
    // If dropdown is open and user is typing, show the search term
    if (isItemsDropdownOpen && itemsSearchTerm) {
      return itemsSearchTerm;
    }
    // If "ALL" is selected, show it
    if (remarksData.remarksBlock === 'ALL') {
      return 'ALL';
    }
    // If "SELECT_REMARKS" is selected, show it
    if (remarksData.remarksBlock === 'SELECT_REMARKS') {
      return 'Select Remarks';
    }
    // If "SELECT_ITEM" is selected, show it as empty to display placeholder
    if (remarksData.remarksBlock === 'SELECT_ITEM' || remarksData.remarksBlock === '') {
      return '';
    }
    // If an item is selected, show it
    if (remarksData.remarksBlock) {
      const selectedItem = blockItemData.find(item => item.item_name === remarksData.remarksBlock);
      if (selectedItem) {
        return selectedItem.item_name;
      }
      // If item not found in blockItemData but remarksBlock has value, return it
      return remarksData.remarksBlock;
    }
    // By default, return empty to show placeholder
    return '';
  };

  // Filter remarks list based on selected Items and Columns
  const filteredRemarksList = useMemo(() => {
    if (!remarksList || remarksList.length === 0) {
      return [];
    }

    // If "SELECT_REMARKS" is selected, return empty array
    if (remarksData.remarksBlock === 'SELECT_REMARKS') {
      return [];
    }

    return remarksList.filter(remark => {
      // Filter by Items (remarksBlock)
      const itemsMatch = remarksData.remarksBlock === 'ALL' ||
        !remarksData.remarksBlock ||
        remark.items === remarksData.remarksBlock;

      // Filter by Columns (remarksItems)
      const columnsMatch = !remarksData.remarksItems ||
        remark.columns === remarksData.remarksItems;

      return itemsMatch && columnsMatch;
    });
  }, [remarksList, remarksData.remarksBlock, remarksData.remarksItems]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (itemsDropdownRef.current && !itemsDropdownRef.current.contains(event.target)) {
        closeItemsDropdown();
      }
    };

    // Add both mouse and touch event listeners for better device compatibility
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  return (
    <>
      <style>
        {`
          .remarks-custom-dropdown {
            -webkit-overflow-scrolling: touch;
            touch-action: pan-y;
          }
          .remarks-custom-dropdown > div {
            -webkit-tap-highlight-color: rgba(0,0,0,0);
          }
          .remarks-section .position-relative {
            -webkit-tap-highlight-color: transparent;
          }
          .remarks-section input {
            touch-action: manipulation;
          }
        `}
      </style>
      
         {/* Remarks Section - Below Block B, same width as Block B */}
         <div className="card">
              <div className="card-body">
                <h5 className="mb-3 border-bottom pb-3" style={{ fontWeight: 'bold' }}>Remarks</h5>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="remarksBlock">Items <span style={{ color: 'red' }}>*</span></label>
                  <div className="position-relative" ref={itemsDropdownRef} style={{ position: 'relative', overflow: 'visible' }}>
                    <input
                      type="text"
                      className="form-control"
                      id="remarksBlock"
                      name="remarksBlock"
                      value={getSelectedItemName()}
                      placeholder={isLoadingBlockItems ? 'Loading...' : 'Select Item'}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Update search term immediately for real-time filtering
                        setItemsSearchTerm(value);
                        // Call debounced search if available
                        if (debouncedBlockItemSearch) {
                          handleItemsSearchChange(value);
                        }
                        // Clear selected item when user types (only if not "ALL" or special values)
                        if (value && remarksData.remarksBlock !== 'ALL' && 
                            remarksData.remarksBlock !== 'SELECT_REMARKS' && 
                            remarksData.remarksBlock !== 'SELECT_ITEM') {
                          setRemarksData(prev => ({
                            ...prev,
                            remarksBlock: ''
                          }));
                        }
                      }}
                      onFocus={() => {
                        if (!isItemsDropdownOpen) {
                          openItemsDropdown();
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!justOpenedItemsRef.current) {
                          toggleItemsDropdown();
                        } else {
                          justOpenedItemsRef.current = false;
                        }
                      }}
                      onMouseDown={() => {
                        if (!isItemsDropdownOpen && !touchStartRef.current) {
                          openItemsDropdown();
                        }
                      }}
                      onTouchStart={(e) => {
                        // Mark as touch interaction
                        touchStartRef.current = true;
                        setTimeout(() => {
                          touchStartRef.current = false;
                        }, 500);
                        
                        // Open dropdown on touch
                        if (!isItemsDropdownOpen) {
                          e.preventDefault(); // Prevent mouse events from firing
                          openItemsDropdown();
                        }
                      }}
                      onTouchEnd={(e) => {
                        // If dropdown is closed, prevent the click event from firing
                        if (!isItemsDropdownOpen) {
                          e.preventDefault();
                        }
                      }}
                      onKeyDown={handleItemsKeyDown}
                      disabled={isLoadingBlockItems}
                      style={{
                        paddingRight: '30px',
                        fontSize: '14px',
                        minHeight: '38px',
                        touchAction: 'manipulation'
                      }}
                    />
                    <i className="fa fa-chevron-down position-absolute"
                      style={{
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#666',
                        pointerEvents: 'none'
                      }}></i>
                    {isItemsDropdownOpen && (
                      <div
                        className="position-absolute bg-white border rounded shadow-sm remarks-custom-dropdown"
                        style={{
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 99999,
                          maxHeight: '200px',
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          minWidth: '250px',
                          width: '100%',
                          maxWidth: '400px',
                          border: '1px solid #ddd',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.15)',
                          position: 'absolute',
                          marginTop: '2px',
                          backgroundColor: '#ffffff',
                          borderRadius: '4px',
                          opacity: 1,
                          WebkitOverflowScrolling: 'touch',
                          touchAction: 'pan-y'
                        }}
                      >
                        {(() => {
                          const availableItems = getAvailableItems();
                          const hasRemarksData = remarksList && remarksList.length > 0;
                          return availableItems.length > 0 ? (
                            <>
                              {/* Always show "Select Item" as first option */}
                              <div
                                key="SELECT_ITEM"
                                ref={(el) => {
                                  if (el) {
                                    itemsItemRefs.current['SELECT_ITEM'] = el;
                                  }
                                }}
                                className="px-3 py-2 cursor-pointer"
                                style={{
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f0f0f0',
                                  fontSize: '12px',
                                  wordWrap: 'break-word',
                                  whiteSpace: 'normal',
                                  minHeight: '40px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  backgroundColor: selectedItemIndex === 0 ? '#e3f2fd' : 'white',
                                  outline: selectedItemIndex === 0 ? '2px solid #1976d2' : 'none',
                                  outlineOffset: '-2px',
                                  touchAction: 'manipulation'
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleItemsItemSelect('SELECT_ITEM');
                                }}
                                onTouchEnd={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleItemsItemSelect('SELECT_ITEM');
                                }}
                                onMouseEnter={() => setSelectedItemIndex(0)}
                                onMouseLeave={() => {
                                  if (selectedItemIndex !== 0) {
                                    setSelectedItemIndex(-1);
                                  }
                                }}
                                tabIndex={-1}
                              >
                                <div style={{
                                  marginBottom: '2px',
                                  wordBreak: 'break-word',
                                  lineHeight: '1.3'
                                }}>
                                  Select Item
                                </div>
                              </div>
                              {/* Always show "ALL" option */}
                              <div
                                key="ALL"
                                ref={(el) => {
                                  if (el) {
                                    itemsItemRefs.current['ALL'] = el;
                                  }
                                }}
                                className="px-3 py-2 cursor-pointer"
                                style={{
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f0f0f0',
                                  fontSize: '12px',
                                  wordWrap: 'break-word',
                                  whiteSpace: 'normal',
                                  minHeight: '40px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  backgroundColor: selectedItemIndex === -1 && remarksData.remarksBlock === 'ALL' ? '#e3f2fd' : (selectedItemIndex === 1 ? '#e3f2fd' : 'white'),
                                  outline: (selectedItemIndex === 1 || (selectedItemIndex === -1 && remarksData.remarksBlock === 'ALL')) ? '2px solid #1976d2' : 'none',
                                  outlineOffset: '-2px',
                                  fontWeight: 'bold',
                                  touchAction: 'manipulation'
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleItemsItemSelect('ALL');
                                }}
                                onTouchEnd={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleItemsItemSelect('ALL');
                                }}
                                onMouseEnter={() => setSelectedItemIndex(1)}
                                onMouseLeave={() => {
                                  if (selectedItemIndex !== 1) {
                                    setSelectedItemIndex(-1);
                                  }
                                }}
                                tabIndex={-1}
                              >
                                <div style={{
                                  fontWeight: 'bold',
                                  marginBottom: '2px',
                                  wordBreak: 'break-word',
                                  lineHeight: '1.3'
                                }}>
                                  ALL
                                </div>
                              </div>
                              {availableItems.map((singleItem, index) => {
                                // +1 for "Select Item", +1 for "ALL" (always at index 0 and 1)
                                const itemIndex = index + 2;
                                const isSelected = selectedItemIndex === itemIndex;
                                return (
                                  <div
                                    key={index}
                                    ref={(el) => {
                                      if (el) {
                                        itemsItemRefs.current[itemIndex] = el;
                                      }
                                    }}
                                    className="px-3 py-2 cursor-pointer"
                                    style={{
                                      cursor: 'pointer',
                                      borderBottom: '1px solid #f0f0f0',
                                      fontSize: '12px',
                                      wordWrap: 'break-word',
                                      whiteSpace: 'normal',
                                      minHeight: '40px',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                      outline: isSelected ? '2px solid #1976d2' : 'none',
                                      outlineOffset: '-2px',
                                      touchAction: 'manipulation'
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleItemsItemSelect(singleItem);
                                    }}
                                    onTouchEnd={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleItemsItemSelect(singleItem);
                                    }}
                                    onMouseEnter={() => setSelectedItemIndex(itemIndex)}
                                    onMouseLeave={() => {
                                      if (!isSelected) {
                                        setSelectedItemIndex(-1);
                                      }
                                    }}
                                    tabIndex={-1}
                                  >
                                    <div style={{
                                      fontWeight: 'bold',
                                      marginBottom: '2px',
                                      wordBreak: 'break-word',
                                      lineHeight: '1.3'
                                    }}>
                                      {singleItem.item_name}
                                    </div>
                                    {singleItem.block_item && (
                                      <div style={{
                                        color: '#666',
                                        fontSize: '11px',
                                        wordBreak: 'break-word',
                                        lineHeight: '1.2'
                                      }}>
                                        {singleItem.block_item}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              {(() => {
                                const availableItems = getAvailableItems();
                                const shouldShowLoadMore = hasMoreBlockItems && availableItems.length >= 10;
                                return shouldShowLoadMore ? (
                                  <div
                                    className="px-3 py-2 text-center text-muted"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      loadMoreBlockItemData();
                                    }}
                                    onTouchEnd={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      loadMoreBlockItemData();
                                    }}
                                    style={{ 
                                      cursor: 'pointer',
                                      touchAction: 'manipulation'
                                    }}
                                  >
                                    {isLoadingMoreBlockItems ? 'Loading more...' : 'Load more items'}
                                  </div>
                                ) : null;
                              })()}
                            </>
                          ) : (
                            <div className="px-3 py-2 text-muted" style={{ fontSize: '12px' }}>
                              {isLoadingBlockItems ? 'Loading items...' : selectedItemsFromTable.length === 0 ? 'Please select items in Types of Assets first' : 'No items found'}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="remarksItems">Columns<span style={{ color: 'red' }}>*</span></label>
                  <select
                    className="form-select"
                    id="remarksItems"
                    name="remarksItems"
                    value={remarksData.remarksItems || ''}
                    onChange={handleChange}
                  >
                      <option value="">Select Column</option>
                    {availableColumns.map(colNum => (
                      <option key={colNum} value={colNum.toString()}>Column {colNum}</option>
                    ))}
                    </select>
                  </div>
                  <div className="col-md-12 mb-3">
                    <label htmlFor="remarks">Remarks<span style={{ color: 'red' }}>*</span></label>
                    <textarea
                      className="form-control"
                      id="remarks"
                      name="remarks"
                    value={remarksData.remarks || ''}
                    onChange={handleChange}
                      rows="4"
                      maxLength="1000"
                      placeholder="Enter Remarks..."
                      style={{ resize: 'vertical' }}
                    />
                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <small className="text-muted">
                        <i className="fa fa-info-circle me-1"></i>
                        Remarks will hold 1000 characters
                      </small>
                      <small className="text-muted">
                      {remarksData.remarks?.length || 0}/1000
                      </small>
                    </div>
                  </div> 
                  {/* <div className="col-md-12 mb-3 text-end">
                    <button className="btn btn-success"><i className="fa fa-save me-2"></i> Save</button>
                  </div> */}
                </div>
            </div>
          </div>


      
    </>
  );
}

export default Remarks;